import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Review,
  ReviewComment,
  ReviewProvider,
  ReviewSeverity,
  ReviewStatus,
} from '@lizrd/contracts';
import { getReviews, simulateReview } from '../lib/api';
import { Card } from '../components/ui/card';
import { Stat } from '../components/ui/stat';
import { Skeleton } from '../components/ui/skeleton';
import { Modal } from '../components/ui/modal';
import { FilterDropdown, type FilterOption } from '../components/ui/filter-dropdown';
import { FilterChip, FiltersPopover } from '../components/ui/filters-popover';
import { Pagination, usePagination } from '../components/ui/pagination';

const PAGE_SIZE = 10;

type TimeSpan = 'week' | 'month' | 'year' | 'all';
const TIME_LABEL: Record<TimeSpan, string> = {
  week: 'This week',
  month: 'This month',
  year: 'This year',
  all: 'All time',
};
const TIME_OPTIONS: FilterOption<TimeSpan>[] = (['week', 'month', 'year', 'all'] as const).map(
  (t) => ({ value: t, label: TIME_LABEL[t] }),
);
/** Epoch-ms cutoff for a time span (calendar-aligned; 0 = all time). */
function timeCutoff(span: TimeSpan): number {
  if (span === 'all') return 0;
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth();
  if (span === 'year') return new Date(y, 0, 1).getTime();
  if (span === 'month') return new Date(y, m, 1).getTime();
  // week — Monday as the first day
  const dow = (d.getDay() + 6) % 7;
  return new Date(y, m, d.getDate() - dow).getTime();
}

const PROVIDER_LABEL: Record<ReviewProvider, string> = { GITHUB: 'GitHub', GITLAB: 'GitLab' };

const STATUS_STYLE: Record<ReviewStatus, { label: string; dot: string; text: string; bg: string }> =
  {
    open: { label: 'Open', dot: 'bg-info', text: 'text-info', bg: 'bg-info-dim' },
    merged: { label: 'Merged', dot: 'bg-success', text: 'text-success', bg: 'bg-success-dim' },
    closed: { label: 'Closed', dot: 'bg-muted', text: 'text-muted', bg: 'bg-surface-2' },
  };

const SEV_STYLE: Record<ReviewSeverity, { label: string; dot: string; text: string }> = {
  high: { label: 'High', dot: 'bg-danger', text: 'text-danger' },
  medium: { label: 'Medium', dot: 'bg-warning', text: 'text-warning' },
  low: { label: 'Low', dot: 'bg-muted', text: 'text-muted' },
};

const SEV_ORDER: ReviewSeverity[] = ['high', 'medium', 'low'];

function fmtDelta(n: number): string {
  const abs = `$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;
  if (n > 0) return `+${abs}/mo`;
  if (n < 0) return `−${abs}/mo`;
  return '$0';
}
function deltaClass(n: number): string {
  if (n > 0) return 'text-danger';
  if (n < 0) return 'text-iris';
  return 'text-muted';
}

/** Clean status pill — a filled dot + label, semantic tokens (not a mono chip). */
function StatusPill({ status }: { status: ReviewStatus }) {
  const s = STATUS_STYLE[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${s.bg} ${s.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

/** Finding counts labelled by severity word (color reinforces, the word disambiguates). */
function FindingCounts({ comments }: { comments: ReviewComment[] }) {
  const counts: Record<ReviewSeverity, number> = { high: 0, medium: 0, low: 0 };
  for (const c of comments) counts[c.severity] += 1;
  const present = SEV_ORDER.filter((s) => counts[s] > 0);
  // A reviewed infra PR with nothing to flag — reassuring, not an em-dash.
  if (present.length === 0) return <span className="text-xs text-success">No issues</span>;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs font-medium">
      {present.map((s) => (
        <span key={s} className={SEV_STYLE[s].text}>
          {counts[s]} {SEV_STYLE[s].label}
        </span>
      ))}
    </div>
  );
}

function addressedOf(review: Review) {
  const total = review.comments.length;
  const done = review.comments.filter((c) => c.addressed).length;
  return { total, done, allDone: total > 0 && done === total };
}

export function ReviewsPage() {
  const { domainId = '' } = useParams();
  const [selected, setSelected] = useState<Review | null>(null);
  const [repo, setRepo] = useState<string>('all');
  const [time, setTime] = useState<TimeSpan>('month');
  const qc = useQueryClient();
  const reviews = useQuery({
    queryKey: ['reviews', domainId],
    queryFn: () => getReviews(domainId),
    enabled: !!domainId,
    // Always refetch when the page is opened so the enabled/disabled state (toggled
    // in Settings) is reflected immediately, independent of the global staleTime.
    refetchOnMount: 'always',
  });
  const sim = useMutation({
    mutationFn: () => simulateReview(domainId),
    // The worker analyzes + persists asynchronously; refetch shortly after so the
    // new review appears without a manual reload.
    onSuccess: () => {
      setTimeout(() => qc.invalidateQueries({ queryKey: ['reviews', domainId] }), 1200);
      setTimeout(() => qc.invalidateQueries({ queryKey: ['reviews', domainId] }), 3000);
    },
  });

  const data = reviews.data;
  const disabled = !!data && !data.enabled;

  // Flatten the repo groups into one PR list, most-recent first.
  const allPrs = useMemo(
    () =>
      (data?.groups ?? [])
        .flatMap((g) => g.reviews)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [data],
  );

  const repoOptions: FilterOption<string>[] = useMemo(() => {
    const byRepo = new Map<string, number>();
    for (const pr of allPrs) byRepo.set(pr.repoFullName, (byRepo.get(pr.repoFullName) ?? 0) + 1);
    return [
      { value: 'all', label: 'All repositories', count: allPrs.length },
      ...[...byRepo.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([name, count]) => ({ value: name, label: name, count })),
    ];
  }, [allPrs]);

  // Repo + time filters scope the whole view — the table, pagination, and the
  // summary strip all read from `filtered`.
  const filtered = useMemo(() => {
    const cutoff = timeCutoff(time);
    const byRepo = repo === 'all' ? allPrs : allPrs.filter((pr) => pr.repoFullName === repo);
    return byRepo.filter((r) => new Date(r.createdAt).getTime() >= cutoff);
  }, [allPrs, repo, time]);

  const pg = usePagination(filtered, PAGE_SIZE);
  // Reset to the first page when a filter narrows the list. `setPage` is a stable
  // useState setter, so this only fires on a filter change.
  const { setPage } = pg;
  useEffect(() => setPage(0), [repo, time, setPage]);

  const summary = useMemo(() => {
    const comments = filtered.flatMap((r) => r.comments);
    return {
      reviewCount: filtered.length,
      commentCount: comments.length,
      wasteCount: comments.filter((c) => c.kind === 'waste').length,
      costDeltaMonthly: filtered.reduce((n, r) => n + r.costDeltaMonthly, 0),
    };
  }, [filtered]);

  const activeFilters = (repo !== 'all' ? 1 : 0) + (time !== 'month' ? 1 : 0);
  const clearFilters = () => {
    setRepo('all');
    setTime('month');
  };

  if (reviews.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const simBtn = data?.canSimulate ? (
    <button
      onClick={() => sim.mutate()}
      disabled={sim.isPending}
      className="flex-none rounded-md border border-border px-3 py-1 text-xs font-medium text-haze transition-colors hover:border-border-strong hover:text-bone disabled:opacity-50"
    >
      {sim.isPending ? 'Generating…' : '+ Simulate a PR review'}
    </button>
  ) : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold text-bone">Reviews</h2>
        <p className="mt-1 text-sm text-muted">
          What Lizrd flagged on your infrastructure pull requests — cost increases and waste
          anti-patterns. Read-only: comments are posted on the PR itself.
        </p>
      </div>

      {disabled && <ReviewsDisabled domainId={domainId} />}

      {data && !disabled && (
        <div className="space-y-2">
          <div className="font-mono text-[0.62rem] font-bold uppercase tracking-wider text-muted">
            {TIME_LABEL[time]}
          </div>
          <Card className="flex flex-wrap divide-x divide-hairline">
            <div className="min-w-[8rem] flex-1 p-4">
              <Stat value={summary.reviewCount} label="PRs reviewed" />
            </div>
            <div className="min-w-[8rem] flex-1 p-4">
              <Stat value={summary.commentCount} label="Findings" />
            </div>
            <div className="min-w-[8rem] flex-1 p-4">
              <Stat value={summary.wasteCount} label="Waste caught" />
            </div>
            <div className="min-w-[8rem] flex-1 p-4">
              <Stat
                value={
                  <span className={deltaClass(summary.costDeltaMonthly)}>
                    {fmtDelta(summary.costDeltaMonthly)}
                  </span>
                }
                label="Net cost impact"
              />
            </div>
          </Card>
        </div>
      )}

      {!disabled && data && allPrs.length === 0 ? (
        <Card className="flex flex-col items-center gap-4 p-8 text-center">
          <p className="text-sm text-muted">
            No reviews yet. Lizrd will comment on infrastructure pull requests as they're opened.
          </p>
          {simBtn}
        </Card>
      ) : (
        !disabled &&
        data && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <FiltersPopover count={activeFilters} onClear={clearFilters}>
                  <FilterDropdown
                    label="Repository"
                    value={repo}
                    options={repoOptions}
                    onChange={setRepo}
                    defaultValue="all"
                  />
                  <FilterDropdown
                    label="Time"
                    value={time}
                    options={TIME_OPTIONS}
                    onChange={setTime}
                    defaultValue="month"
                  />
                </FiltersPopover>
                {repo !== 'all' && <FilterChip label={repo} onClear={() => setRepo('all')} />}
                {time !== 'month' && (
                  <FilterChip label={TIME_LABEL[time]} onClear={() => setTime('month')} />
                )}
              </div>
              {simBtn}
            </div>

            {filtered.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-sm text-muted">
                  No reviews in {TIME_LABEL[time].toLowerCase()}.
                </p>
              </Card>
            ) : (
              <>
                <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-hairline">
                          <Th>Status</Th>
                          <Th>Pull request</Th>
                          <Th>Findings</Th>
                          <Th className="text-right">Cost impact</Th>
                          <Th className="text-right">Addressed</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {pg.pageItems.map((pr) => {
                          const a = addressedOf(pr);
                          return (
                            <tr
                              key={pr.id}
                              onClick={() => setSelected(pr)}
                              className="cursor-pointer border-b border-hairline transition-colors last:border-0 hover:bg-surface-2/50"
                            >
                              <td className="px-4 py-3 align-middle">
                                <StatusPill status={pr.status} />
                              </td>
                              <td className="px-4 py-3 align-middle">
                                <div className="font-medium text-bone">{pr.title}</div>
                                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 font-mono text-[0.68rem] text-muted">
                                  <span>#{pr.prNumber}</span>
                                  <span>·</span>
                                  <span>{pr.repoFullName}</span>
                                  <span className="text-haze">{PROVIDER_LABEL[pr.provider]}</span>
                                  {pr.author && <span>· {pr.author}</span>}
                                </div>
                              </td>
                              <td className="px-4 py-3 align-middle">
                                <FindingCounts comments={pr.comments} />
                              </td>
                              <td
                                className={`px-4 py-3 text-right align-middle font-mono font-semibold ${deltaClass(pr.costDeltaMonthly)}`}
                              >
                                {fmtDelta(pr.costDeltaMonthly)}
                              </td>
                              <td className="px-4 py-3 text-right align-middle">
                                <span
                                  className={`font-mono text-xs ${a.allDone ? 'text-success' : 'text-muted'}`}
                                >
                                  {a.allDone ? '✓ ' : ''}
                                  {a.done}/{a.total}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Pagination
                  page={pg.page}
                  pageCount={pg.pageCount}
                  total={pg.total}
                  start={pg.start}
                  shown={pg.shown}
                  onPrev={pg.prev}
                  onNext={pg.next}
                />
              </>
            )}
          </div>
        )
      )}

      <ReviewModal review={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={`px-4 py-2.5 text-left font-mono text-[0.62rem] font-bold uppercase tracking-wider text-muted ${className}`}
    >
      {children}
    </th>
  );
}

function ReviewModal({ review, onClose }: { review: Review | null; onClose: () => void }) {
  if (!review) return null;
  const a = addressedOf(review);
  return (
    <Modal
      open={!!review}
      onClose={onClose}
      maxWidth="3xl"
      title={
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill status={review.status} />
            <span className="text-sm font-semibold text-bone">{review.title}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 font-mono text-[0.68rem] text-muted">
            <span>#{review.prNumber}</span>
            <span>·</span>
            <span>{review.repoFullName}</span>
            <span className="text-haze">{PROVIDER_LABEL[review.provider]}</span>
            {review.author && <span>· {review.author}</span>}
            {review.url && (
              <a
                href={review.url}
                target="_blank"
                rel="noreferrer"
                className="font-sans font-medium text-iris hover:underline"
              >
                · View PR ↗
              </a>
            )}
          </div>
        </div>
      }
    >
      <div className="mb-3 flex items-center justify-between text-xs text-muted">
        <span>
          {review.comments.length} finding{review.comments.length === 1 ? '' : 's'}
        </span>
        <span className={a.allDone ? 'text-success' : ''}>
          {a.done}/{a.total} addressed
        </span>
      </div>
      <div className="space-y-2.5">
        {review.comments.map((c) => (
          <CommentCard key={c.id} comment={c} />
        ))}
      </div>
    </Modal>
  );
}

function CommentCard({ comment }: { comment: ReviewComment }) {
  const sev = SEV_STYLE[comment.severity];
  return (
    <div className="rounded-md border border-hairline bg-surface-2/40 p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${sev.text}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${sev.dot}`} />
            {sev.label}
          </span>
          <span className="font-mono text-[0.62rem] uppercase tracking-wide text-muted">
            {comment.kind === 'waste' ? 'Waste' : 'Cost'}
          </span>
        </div>
        <AddressedTag addressed={comment.addressed} />
      </div>
      <div className="mt-2 text-sm font-medium text-bone">{comment.title}</div>
      <p className="mt-1 text-xs leading-relaxed text-muted">{comment.rationale}</p>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[0.66rem] text-muted">
          {comment.filePath && <span>{comment.filePath}</span>}
          {comment.resourceName && <span className="text-haze">{comment.resourceName}</span>}
          <span>risk: {comment.riskLevel}</span>
        </div>
        {comment.costDeltaMonthly !== 0 && (
          <span
            className={`font-mono text-sm font-semibold ${deltaClass(comment.costDeltaMonthly)}`}
          >
            {fmtDelta(comment.costDeltaMonthly)}
          </span>
        )}
      </div>
    </div>
  );
}

/** A quiet, clear "addressed / open" marker. */
function AddressedTag({ addressed }: { addressed: boolean }) {
  if (addressed) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
        <span aria-hidden>✓</span> Addressed
      </span>
    );
  }
  return <span className="text-xs text-muted">Open</span>;
}

/** Shown when reviews are off for the domain: explains the capability and links
 * to the settings section (which auto-scrolls to it) to turn it on. */
function ReviewsDisabled({ domainId }: { domainId: string }) {
  return (
    <Card className="p-8">
      <div className="mx-auto max-w-xl text-center">
        <h3 className="font-display text-lg font-bold text-bone">
          Catch cost &amp; waste before they merge
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          When enabled, Lizrd reviews every infrastructure pull request on GitHub &amp; GitLab and
          comments — before merge — with the cost impact and waste anti-patterns, grounded in your
          knowledge graph. The findings land here, one row per pull request.
        </p>
        <div className="mx-auto mt-5 grid max-w-md gap-2 text-left">
          <Feature
            title="Cost impact on every PR"
            desc="See the $/mo a change adds before it ships."
          />
          <Feature
            title="Waste anti-patterns"
            desc="gp2→gp3, over-provisioned volumes, missing autoscaling."
          />
          <Feature
            title="Read-only &amp; grouped"
            desc="Open a PR to see each finding and whether it was addressed."
          />
        </div>

        <div className="mx-auto mt-5 max-w-md rounded-md border border-hairline bg-surface-2/30 p-3.5 text-left text-xs leading-relaxed text-muted">
          <div className="mb-1.5 font-mono text-[0.62rem] font-bold uppercase tracking-wider text-muted">
            What Lizrd needs
          </div>
          <p>
            <span className="font-semibold text-bone">GitHub</span> — the Lizrd GitHub App with
            permission to post checks &amp; comments on pull requests. If it&apos;s missing, we show
            you exactly what to grant.
          </p>
          <p className="mt-1.5">
            <span className="font-semibold text-bone">GitLab</span> — a project or group token with
            the <code className="rounded bg-surface px-1">api</code> scope so Lizrd can comment on
            merge requests; Lizrd sets up the webhook for you.
          </p>
          <p className="mt-1.5 text-haze">
            Read-only to your code — Lizrd reads it to analyze a change and never modifies it.
          </p>
        </div>

        <Link
          to={`/domains/${domainId}/settings#reviews`}
          className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-iris px-4 py-2 text-sm font-semibold text-on-iris transition-colors hover:bg-iris-strong"
        >
          Enable reviews in Settings →
        </Link>
        <p className="mt-3 text-xs text-muted">
          Reviews are currently off for this domain. An administrator or manager can turn them on.
        </p>
      </div>
    </Card>
  );
}

function Feature({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="flex items-start gap-2.5 rounded-md border border-hairline bg-surface-2/40 px-3 py-2.5">
      <span className="mt-0.5 text-iris" aria-hidden>
        ✓
      </span>
      <div>
        <div className="text-sm font-medium text-bone">{title}</div>
        <div className="text-xs text-muted">{desc}</div>
      </div>
    </div>
  );
}
